import {ofType} from 'redux-observable';
import {filter, debounceTime, merge, tap, ignoreElements} from 'rxjs/operators';
import {actionTypes as reduxActionTypes, getFormNames} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import * as actionTypes from 'web-app/react/constants/action-types';
import {type RootState} from 'web-app/react/redux/root-state';
import LocalStorageService from 'web-app/services/local-storage';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {serialize} from 'web-app/react/components/file-upload/file-record';

import * as Constants from './constants';
import * as Selectors from './selectors';

// State selection
const conversationId = (state: RootState) => state.inbox.inboxMain.conversationId;
const valuesToDraft = (state: RootState) => {
    const selector = Selectors.makeValuesToDraft() as any;
    return selector(state, {conversationId: conversationId(state)});
};

// Drafting mechanism
const handleDraftValueChange = (conversationId: string, values: ReturnType<typeof valuesToDraft>) => {
    const hasValuesToDraft =
        hasValue(values) &&
        ((hasValue(values.formValues) && values.formValues.body) ||
            (hasValue(values.files) && values.files.length) ||
            (hasValue(values.selectedReceivers) && Object.keys(values.selectedReceivers).length > 0));

    if (hasValuesToDraft) {
        const serializedValues = {
            ...values,
            files: values.files?.map(serialize) ?? undefined,
            sendAsSms: false,
        };

        LocalStorageService.saveForUserContextEncrypted(conversationId, serializedValues, false);
    } else {
        LocalStorageService.clearForUserContext(conversationId);
    }
};

// Epics for listening to actions that would trigger a draft
const listenForFormChanges = action$ =>
    action$.pipe(
        ofType(reduxActionTypes.CHANGE),
        filter(({meta}) => meta.form === Constants.FORM),
    );

const listenForFileChanges = action$ =>
    action$.pipe(
        ofType(
            actionTypes.FILE_UPLOAD_CLEAR_FILES,
            actionTypes.FILE_UPLOAD_REMOVE_FILE,
            actionTypes.FILE_UPLOAD_FILE_UPLOAD_SUCCESS,
        ),
        filter(({payload}) => payload.formName === Constants.FORM),
    );

const listenForChanges = action$ => merge(listenForFileChanges(action$), listenForFormChanges(action$));

// Epic for triggering a draft
const draftMessageOnChange = (action$, state$) =>
    action$.pipe(
        filter(() => {
            const conversationIdFromState = conversationId(state$.value);
            return hasValue(conversationIdFromState) && conversationIdFromState !== '';
        }),
        listenForChanges(action$),
        debounceTime(500),
        // Cancel the observable if the form has been unmounted and its redux state doesn't exist anymore.
        filter(action => Boolean(action && getFormNames()(state$.value).includes(Constants.FORM))),
        tap(() => handleDraftValueChange(conversationId(state$.value), valuesToDraft(state$.value))),
        ignoreElements(),
    );

export default combineEpics(draftMessageOnChange);
